// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { isEmpty, forEach, filter, has, omit, assign, map, flow } from 'lodash/fp';
import { observable, computed, action, toJS } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';
import QuerySourceTree from './Izenda.QuerySourceTree';
import constants from 'logic/data/Izenda.ConnectionConstants';
import commonMessages from 'share/messages/Izenda.CommonMessages';
import IzendaRequest, { IzendaRequestHeader, IZENDA_REQUEST_BODY_LANGUAGES } from 'logic/models/Izenda.Request';

@tracker
class ConnectionDetail {

  endpointModels = [];

  @observable id = null;
  @observable connectionString = '';
  @observable previousConnectionStringHasDataSource = '';
  @observable name = '';
  @observable serverTypeId = constants.SERVER_TYPE.MSSQL; // default is MSSQL
  @observable serverTypeName = '';
  @observable visible = true;
  @observable isSelected = false;
  @observable change = 0;
  @observable dbSource = [];
  @observable connectionStringChanged = false;
  @observable isNewConnectionStringWithoutUpdateDataSource = true;
  @observable isCurrentConnectionStringSameAsSavedConnectionString = true;
  @observable isConnectionStringValid = false;
  @observable isAvailableDataSourceChange = false;
  @observable alertMessage = commonMessages.EMPTY_MESSAGE;
  @observable request = new IzendaRequest([
      new IzendaRequestHeader(true, 'header1', 'header value 1'),
      new IzendaRequestHeader(false, 'header2', 'header value 2')
    ],
`{
  "Chart":"0",
  "Form":"1",
  "Gauge":"2",
  "Grid":"3",
  "Map":"4"
}`,
    IZENDA_REQUEST_BODY_LANGUAGES.JSON);

  //Derived state props
  @computed get isConnectionStringReady() {
    return this.visible
      && !isEmpty(this.connectionString)
      && this.isConnectionStringValid;
  }
  @computed get isSecured() {
    return this.isConnectionStringValid;
  }
  @computed get securedConnectionString() {
    return this.isSecured
      ? constants.CONNECTION_STRING_HASH
      : this.connectionString;
  }

  @computed get requestDetail() {
    return this.request || new IzendaRequest([], '', IZENDA_REQUEST_BODY_LANGUAGES.TEXT);
  }

  @computed get availableQuerySourceTree() {
    return new QuerySourceTree(this.dbSource);
  }
  @computed get selectedQuerySourceTree() {
    return new QuerySourceTree(this.dbSource);
  }
  @computed get isNew() {
    return !this.id;
  }
  @computed get haveDataSource() {
    return !isEmpty(this.dbSource);
  }
  @computed get needConfirmMessageForAvailableDatasouceChange() {
    return !this.isNew && this.isAvailableDataSourceChange;
  }
  @computed get needConfirmWhenLeaving() {
    return this.isDirty && this.visible;
  }
  @computed get canSave() {
    return this.isDirty || this.change !== 0;
  }
  @computed get notNeedToValidateSchema() {
    //don't have data souce or already click connect
    // or new connection string is match with previous connection string already load datasource
    return !this.haveDataSource ||
      !this.connectionStringChanged ||
      !this.isNewConnectionStringWithoutUpdateDataSource ||
      (this.isNew && this.connectionString === this.previousConnectionStringHasDataSource) ||
      this.isCurrentConnectionStringSameAsSavedConnectionString ||
      this.isNewSchemaMatchPrevious;
  }
  @computed get needReloadDetectChange() {
    return !this.isNew;
  }

  /**
   * Connection detail validation constraints
   * @type {Object}
   */
  static get constraints() {
    return {
      name: constants.DATABASE_NAME_CONSTRAINT,
      connectionString: constants.CONNECTION_STRING_CONSTRAINT
    }
  }

  /**
   * Read connection detail data (mostly this data comes from API).
   * @param  {Object} detail
   */
  @action('readDetail')
  readDetail(detail: any) {
    this.id = detail.id;
    this.connectionString = detail.connectionString;
    this.name = detail.name;
    this.serverTypeId = detail.serverTypeId;
    this.visible = detail.visible;
    this.change = detail.physicalChange;
    this.isConnectionStringValid = !!detail.id;
    this.connectionStringChanged = false;

    if(detail.serverTypeName){
      this.serverTypeName = detail.serverTypeName;
    }
    this.resetDirty();
    this.resetAvailableDataSourceChange();
  }

  resetAvailableDataSourceChange() {
    this.isAvailableDataSourceChange = false;
  }

  markAvailableDataSourceChange() {
    this.isAvailableDataSourceChange = true;
  }

  /**
   * Read data sources (mostly this data comes from API)
   * @param  {Object} dbSource
   */
  @action('readDbSource')
  readDbSource(dbSource: any) {
    const querySources = dbSource && forEach((schema) => {
      schema.querySources = filter((detail) => {
        return !(detail.type === constants.QUERY_SOURCE_TYPE.VIEW && detail.isCustomQuerySource)}
        , schema.querySources);
    }, dbSource.querySources);
    this.dbSource = querySources || [];
    this.isNewConnectionStringWithoutUpdateDataSource = false;

    //for new item copy current connection to previous connection for test
    this.isNew && this.saveCurrentToPreviousConnectionStringHasDataSource()

    this.resetAvailableDataSourceChange();
  }

  /**
   * Reset status when load saved connection
   */
  @action('setConnectionStringStatus')
  setConnectionStringStatus() {
    this.isCurrentConnectionStringSameAsSavedConnectionString = true;
    this.isNewSchemaMatchPrevious = true;
  }

  @action('setRequest')
  setRequest(request: IzendaRequest) {
    this.request = request;
    this.markDirty();
  }

  /**
   * Set previous connection string that alreay connect
   */
  saveCurrentToPreviousConnectionStringHasDataSource() {
    this.previousConnectionStringHasDataSource = this.connectionString;
  }

  /**
   * Read test result to know new connection is the same as saved connection string
   * @param  {Object} result receive from api
   */
  readTestResult(result: any) {
    //this.isCurrentConnectionStringSameAsSavedConnectionString = !result.differentDatabase;
  }

  /**
   * Read test result to know new connection is the same schema as saved connection
   * @param  {Boolean} result receive from api
   */
  readStatusValidateSchema(result): boolean {
    this.isNewSchemaMatchPrevious = result;
  }

  /**
   * Set new connection string
   * @param {String} val New connection string
   */
  @action('setConnectionString')
  setConnectionString(val: string){
    this.connectionStringChanged = this.connectionString !== val || isEmpty(val);
    this.isConnectionStringValid = !this.connectionStringChanged;
    this.isNewConnectionStringWithoutUpdateDataSource = true;
    this.isCurrentConnectionStringSameAsSavedConnectionString = false;
    this.isNewSchemaMatchPrevious = false;
    this.connectionString = val;
    this.markDirty();
  }

  /**
   * Toggle visibility of connection
   * @return {[type]} [description]
   */
  toggleVisibility () {
    this.visible = !this.visible;
  }

  /**
   * Set new connection name
   * @param {String} val New connection name value
   */
  @action('setName')
  setName(val: string) {
    this.name = val;
    this.markDirty();
  }

  /**
   * Mark connection string is valid or not.
   * @param {Boolean} isValid
   */
  setConnectionStringValid(isValid: boolean) {
    this.isConnectionStringValid = isValid;
  }

  /**
   * Set database server type id.
   * @param {String} val New database server type id (UUID string)
   */
  @action('setServerTypeId')
  setServerTypeId(val: string) {
    this.serverTypeId = val;
    this.markDirty();
  }

  setChange(val: number) {
    this.change = val;
  }

  /**
   * Return data object that represents for a connection. It consists only
   * selected query source object.
   */
  getData(forValidation: boolean) {
    const take = (source) => {
      return !forValidation && (!has('selected', source) || source.selected );
    }
    const getSelectedChild = (querySource) => {
      const children = querySource.querySources;
      const restProps = omit('querySources', querySource);
      return assign(restProps, {
        querySources: children ? flow(filter(take), map(getSelectedChild))(children) : null
      }, {});
    }
    const querySources = toJS(flow(filter(take), map(getSelectedChild))(this.dbSource));

    return {
      id: this.id,
      name: this.name,
      serverTypeId: this.serverTypeId,
      connectionString: this.connectionString,
      visible: this.visible,
      dBSource: {
        querySources: isEmpty(querySources) ? [] : querySources
      }
    }
  }

  resetModelError() {
    this.setAlertMessage(commonMessages.EMPTY_MESSAGE);
    this.setConnectionStringValid(true);
  }

  setAlertMessage(newAlertMessage: any) {
    this.alertMessage = newAlertMessage;
  }
}

export default ConnectionDetail;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ConnectionDetail.js